/**
 * Lo que se pega en una app para que aparezca el chat de soporte.
 *
 * El esquema es el de siempre con una clave de servicio: la clave `ssk_…` vive
 * en el **backend** de la app (`SIETE_SOPORTE_KEY`) y nunca llega al navegador.
 * El backend, con el usuario ya logueado, le pide a siete-soporte un pase de
 * diez minutos para esa persona; el widget lo usa para hablar con el chat.
 */

/** siete-soporte en producción: de acá salen el widget y los pases. */
export const SOPORTE_URL = 'https://soporte.wearesiete.com'

/** La versión congelada del widget que se instala: con SRI, no cambia nunca. */
export const WIDGET_VERSION = 'widget.v3.js'

/**
 * El hash de `widget.v3.js`: el navegador se niega a correr otro archivo con
 * ese nombre. Un cambio del widget es un `widget.v4.js` con su hash. v3: la
 * cara de Siete en el botón y el número del ticket al confirmar.
 */
export const WIDGET_SRI = 'sha384-gOCA7VrZ8SJQ7rcAwBjjcUNpF5RoX27hulka/G5/SH0x8WrIfDmSLCxMvgl+ZfiX'

/** La ruta del backend de la app que entrega el pase. Es una sugerencia: puede ser otra. */
export const RUTA_DEL_PASE = '/api/soporte/pase'

export type Backend = 'fastapi' | 'next' | 'express'

export const BACKENDS: readonly { value: Backend; label: string }[] = [
  { value: 'fastapi', label: 'FastAPI' },
  { value: 'next', label: 'Next.js' },
  { value: 'express', label: 'Express' },
]

export function envSnippet(key: string): string {
  return `SIETE_SOPORTE_URL=${SOPORTE_URL}\nSIETE_SOPORTE_KEY=${key}`
}

export function routeSnippet(backend: Backend): string {
  if (backend === 'fastapi') {
    return `import os

import httpx
from fastapi import APIRouter, Depends

router = APIRouter()


@router.post("${RUTA_DEL_PASE}")
def support_pass(user=Depends(current_user)):  # the login the app already has
    response = httpx.post(
        f"{os.environ['SIETE_SOPORTE_URL']}/api/v1/sesiones",
        headers={"X-API-Key": os.environ["SIETE_SOPORTE_KEY"]},
        json={"id": str(user.id), "email": user.email, "name": user.name},
        timeout=5,
    )
    response.raise_for_status()
    return {"token": response.json()["token"]}`
  }
  if (backend === 'next') {
    return `// app${RUTA_DEL_PASE}/route.ts
import { NextResponse } from 'next/server'

export async function POST() {
  const user = await currentUser() // the login the app already has
  if (!user) return NextResponse.json({ error: 'unauthorized' }, { status: 401 })

  const response = await fetch(\`\${process.env.SIETE_SOPORTE_URL}/api/v1/sesiones\`, {
    method: 'POST',
    headers: {
      'X-API-Key': process.env.SIETE_SOPORTE_KEY!,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: user.id, email: user.email, name: user.name }),
  })
  if (!response.ok) return NextResponse.json({ error: 'support unavailable' }, { status: 502 })
  const { token } = await response.json()
  return NextResponse.json({ token })
}`
  }
  return `app.post('${RUTA_DEL_PASE}', requireAuth, async (req, res) => {
  // requireAuth: the login the app already has
  const response = await fetch(\`\${process.env.SIETE_SOPORTE_URL}/api/v1/sesiones\`, {
    method: 'POST',
    headers: {
      'X-API-Key': process.env.SIETE_SOPORTE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: req.user.id, email: req.user.email, name: req.user.name }),
  })
  if (!response.ok) return res.status(502).json({ error: 'support unavailable' })
  const { token } = await response.json()
  res.json({ token })
})`
}

/** La configuración va antes del script: el widget la lee al cargar. */
export function widgetSnippet(slug: string): string {
  return `<script>
  window.SieteSoporteConfig = {
    app: '${slug}',
    getToken: () =>
      fetch('${RUTA_DEL_PASE}', { method: 'POST', credentials: 'include' })
        .then((r) => r.json())
        .then((r) => r.token),
  }
</script>
<script
  src="${SOPORTE_URL}/${WIDGET_VERSION}"
  integrity="${WIDGET_SRI}"
  crossorigin="anonymous"
  async
></script>`
}

/** `Outply Web` → `outply-web`: lo que siete-soporte acepta como slug. */
export function slugDe(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
}
